import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Bell, AlertTriangle, Clock, CheckCircle, UserX, RefreshCw,
  MessageCircle, Eye, EyeOff, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const AlertsReminders = () => {
  const [alerts, setAlerts] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [alertsRes, remindersRes] = await Promise.all([
        axios.get(`${API_URL}/api/alerts`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reminders`, { withCredentials: true })
      ]);
      setAlerts(alertsRes.data);
      setReminders(remindersRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDetectNoShows = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/alerts/detect-no-shows`, {}, { withCredentials: true });
      toast.success(`${data.new_alerts} novos alertas detectados`);
      fetchData();
    } catch (error) {
      toast.error('Erro ao verificar faltas');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessReminders = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/reminders/process`, {}, { withCredentials: true });
      toast.success(`${data.processed_count} lembretes processados`);
      fetchData();
    } catch (error) {
      toast.error('Erro ao processar lembretes');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (alertId) => {
    try {
      await axios.put(`${API_URL}/api/alerts/${alertId}/read`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/alerts/read-all`, {}, { withCredentials: true });
      toast.success('Todos os alertas marcados como lidos');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const unreadAlerts = alerts.filter(a => !a.is_read);
  const pendingReminders = reminders.filter(r => r.status === 'pending');
  const sentReminders = reminders.filter(r => r.status !== 'pending');

  const severityConfig = {
    critical: { bg: 'bg-[#A8174E]/10', border: 'border-[#A8174E]', icon: AlertTriangle, color: 'text-[#A8174E]' },
    warning: { bg: 'bg-[#E9C46A]/10', border: 'border-[#E9C46A]', icon: UserX, color: 'text-[#E9C46A]' },
    info: { bg: 'bg-blue-50', border: 'border-blue-300', icon: Bell, color: 'text-blue-600' }
  };

  return (
    <div className="space-y-6" data-testid="alerts-reminders">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Alertas & Lembretes
          </h2>
          <p className="text-[#5F6B85]">Faltas, lembretes de consulta e notificações</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDetectNoShows}
            disabled={loading}
            className="border-[#E5DCC8]"
            data-testid="detect-no-shows-button"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Verificar Faltas
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleProcessReminders}
            disabled={loading}
            className="border-[#E5DCC8]"
            data-testid="process-reminders-button"
          >
            <Send className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Enviar Lembretes
          </Button>
          {unreadAlerts.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllRead}
              className="border-[#E5DCC8]"
              data-testid="mark-all-read-button"
            >
              <Eye className="h-4 w-4 mr-1" />
              Marcar lidos
            </Button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-[#E5DCC8]" data-testid="unread-alerts-count">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-[#A8174E]/10 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-[#A8174E]" />
            </div>
            <div>
              <p className="text-xs text-[#7A85A0]">Alertas Pendentes</p>
              <p className="text-xl font-bold text-[#A8174E]">{unreadAlerts.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5DCC8]" data-testid="pending-reminders-count">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-[#E9C46A]/10 rounded-lg">
              <Clock className="h-5 w-5 text-[#E9C46A]" />
            </div>
            <div>
              <p className="text-xs text-[#7A85A0]">Lembretes Pendentes</p>
              <p className="text-xl font-bold text-[#E9C46A]">{pendingReminders.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5DCC8]" data-testid="sent-reminders-count">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-[#0F1B3D]/10 rounded-lg">
              <CheckCircle className="h-5 w-5 text-[#0F1B3D]" />
            </div>
            <div>
              <p className="text-xs text-[#7A85A0]">Lembretes Enviados</p>
              <p className="text-xl font-bold text-[#0F1B3D]">{sentReminders.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5DCC8]" data-testid="total-alerts-count">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-[#0F1B3D]/10 rounded-lg">
              <Bell className="h-5 w-5 text-[#0F1B3D]" />
            </div>
            <div>
              <p className="text-xs text-[#7A85A0]">Total de Alertas</p>
              <p className="text-xl font-bold text-[#0F1B3D]">{alerts.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="alerts" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            Alertas {unreadAlerts.length > 0 && <span className="ml-1 bg-[#A8174E] text-white text-xs rounded-full px-1.5">{unreadAlerts.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Lembretes Pendentes {pendingReminders.length > 0 && <span className="ml-1 bg-[#E9C46A] text-white text-xs rounded-full px-1.5">{pendingReminders.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent">Enviados</TabsTrigger>
        </TabsList>

        <TabsContent value="alerts">
          <div className="space-y-3">
            {alerts.length === 0 ? (
              <Card className="border-[#E5DCC8]">
                <CardContent className="p-8 text-center">
                  <CheckCircle className="h-12 w-12 text-[#0F1B3D] mx-auto mb-3" />
                  <p className="text-[#7A85A0]">Nenhum alerta. Tudo em ordem!</p>
                  <p className="text-sm text-[#7A85A0] mt-1">Clique em "Verificar Faltas" para detectar no-shows</p>
                </CardContent>
              </Card>
            ) : (
              alerts.map((alert) => {
                const config = severityConfig[alert.severity] || severityConfig.info;
                const Icon = config.icon;
                return (
                  <Card key={alert.alert_id} className={`${alert.is_read ? 'border-[#E5DCC8] opacity-60' : config.border} border`} data-testid={`alert-${alert.alert_id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 ${config.bg} rounded-lg mt-0.5`}>
                            <Icon className={`h-5 w-5 ${config.color}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded ${config.bg} ${config.color}`}>
                                {alert.severity === 'critical' ? 'Crítico' : alert.severity === 'warning' ? 'Atenção' : 'Info'}
                              </span>
                              {alert.type === 'repeated_no_show' && (
                                <span className="text-xs bg-[#A8174E] text-white px-2 py-0.5 rounded">
                                  {alert.no_show_count}x faltas
                                </span>
                              )}
                            </div>
                            <p className="font-medium text-[#0F1B3D]">{alert.message}</p>
                            <p className="text-xs text-[#7A85A0] mt-1">
                              {new Date(alert.created_at).toLocaleString('pt-BR')}
                            </p>
                          </div>
                        </div>
                        {!alert.is_read && (
                          <Button variant="ghost" size="sm" onClick={() => handleMarkRead(alert.alert_id)} className="h-8 w-8 p-0" title="Marcar como lido" data-testid={`read-alert-${alert.alert_id}`}>
                            <EyeOff className="h-4 w-4 text-[#7A85A0]" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        <TabsContent value="pending">
          <div className="space-y-3">
            {pendingReminders.length === 0 ? (
              <Card className="border-[#E5DCC8]">
                <CardContent className="p-8 text-center">
                  <Clock className="h-12 w-12 text-[#7A85A0] mx-auto mb-3" />
                  <p className="text-[#7A85A0]">Nenhum lembrete pendente</p>
                </CardContent>
              </Card>
            ) : (
              pendingReminders.map((rem) => (
                <ReminderCard key={rem.reminder_id} reminder={rem} />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="sent">
          <div className="space-y-3">
            {sentReminders.length === 0 ? (
              <Card className="border-[#E5DCC8]">
                <CardContent className="p-8 text-center">
                  <Send className="h-12 w-12 text-[#7A85A0] mx-auto mb-3" />
                  <p className="text-[#7A85A0]">Nenhum lembrete enviado ainda</p>
                </CardContent>
              </Card>
            ) : (
              sentReminders.map((rem) => (
                <ReminderCard key={rem.reminder_id} reminder={rem} />
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const ReminderCard = ({ reminder }) => {
  const isPatient = reminder.target === 'patient';
  const isPending = reminder.status === 'pending';

  return (
    <Card className="border-[#E5DCC8]" data-testid={`reminder-${reminder.reminder_id}`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className={`p-2 ${isPatient ? 'bg-[#0F1B3D]/10' : 'bg-[#B8845C]/10'} rounded-lg mt-0.5`}>
            {isPatient
              ? <MessageCircle className="h-5 w-5 text-[#0F1B3D]" />
              : <Bell className="h-5 w-5 text-[#B8845C]" />
            }
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-medium px-2 py-0.5 rounded ${isPatient ? 'bg-[#0F1B3D]/10 text-[#0F1B3D]' : 'bg-[#B8845C]/10 text-[#B8845C]'}`}>
                {isPatient ? 'Paciente (WhatsApp)' : 'Doutora (Sistema)'}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded ${isPending ? 'bg-[#E9C46A]/10 text-[#E9C46A]' : 'bg-[#0F1B3D]/10 text-[#0F1B3D]'}`}>
                {isPending ? 'Pendente' : reminder.status === 'sent_mock' ? 'Enviado (Mock)' : 'Enviado'}
              </span>
            </div>
            <p className="text-sm text-[#0F1B3D] font-medium">{reminder.patient_name}</p>
            <p className="text-sm text-[#5F6B85] mt-1">{reminder.message}</p>
            <div className="flex gap-4 mt-2 text-xs text-[#7A85A0]">
              <span>Agendado: {new Date(reminder.scheduled_for).toLocaleString('pt-BR')}</span>
              {reminder.processed_at && <span>Enviado: {new Date(reminder.processed_at).toLocaleString('pt-BR')}</span>}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
